import { log } from './config';
import type { Config, DecisionResponse } from './types';

/**
 * Own-properties-only copy of a parsed JSON value (#48, #75).
 *
 * We execute inside pages we do not own, and `JSON.parse` hands back objects
 * that inherit from `Object.prototype`. A host page that pollutes it — directly
 * or through one of the many libraries that have — makes every field the
 * response *omits* read as whatever the pollution says. `fallback.remove` was
 * the case that reached production review (#48) and it was fixed with a
 * per-property `hasOwnProperty` check; #75 is every other read, and a second
 * per-property check is the version where somebody adds a field and forgets.
 *
 * So the guard is structural and lives at the boundary instead: nothing
 * downstream reads a raw parsed object at all. Each object becomes a
 * `Object.create(null)` carrying only its own enumerable keys, recursively, so
 * an absent field is `undefined` no matter what the page's prototype holds and
 * no matter which fields the API grows later. A `"__proto__"` key in the body
 * lands as an ordinary own property, because a null-prototype object has no
 * setter to invoke.
 *
 * This also stops a serialisation detail of `mdfs-serve` being load-bearing.
 * Nothing here is reachable from our own responses today, and the only reason
 * is that the pydantic models happen to emit every key as an own key — true,
 * and enforced nowhere. A field gaining `exclude_none`, turning optional, or
 * being written by some future non-pydantic path would have un-fixed #48
 * silently.
 */
export function ownProperties<T>(value: T): T {
  if (!value || typeof value !== 'object') return value;
  if (value instanceof Array) return value.map(ownProperties) as unknown as T;
  const src = value as unknown as Record<string, unknown>;
  const out = Object.create(null) as Record<string, unknown>;
  const keys = Object.keys(src);
  for (let i = 0; i < keys.length; i++) out[keys[i]] = ownProperties(src[keys[i]]);
  return out as unknown as T;
}

/**
 * POST /v1/decision (plan §7.1) with an AbortController timeout.
 *
 * Returns null on timeout, network error, non-2xx, or unparseable body — every
 * failure mode funnels into the same fallback path in mdfs.ts. The API is
 * specified to answer 200 even for no-fill, so `null` genuinely means "the
 * service did not answer usefully".
 */
export function decide(
  cfg: Config,
  body: unknown,
  timeoutMs: number,
): Promise<DecisionResponse | null> {
  const ac = typeof AbortController === 'function' ? new AbortController() : null;
  const timer = setTimeout(() => {
    log('timeout', timeoutMs);
    if (ac) ac.abort();
  }, timeoutMs);

  const opts: RequestInit = {
    method: 'POST',
    // No cookies to the decision API — the session id in the payload is the
    // only identifier, and it is first-party and opaque (plan §14).
    credentials: 'omit',
    cache: 'no-store',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  };
  if (ac) opts.signal = ac.signal;

  // `api!`: core.ts refuses to serve a test when no API base is configured
  // (#51), so this is only ever reached with a string — '' for same origin.
  return fetch(cfg.api! + '/v1/decision', opts)
    .then((r) => {
      if (!r.ok) throw new Error('http ' + r.status);
      return r.json();
    })
    // Every read of the body downstream goes through this one copy — see
    // `ownProperties`. Nothing else is allowed to touch the parsed object.
    .then((json) => (json && typeof json === 'object' ? ownProperties(json as DecisionResponse) : null))
    .catch((err) => {
      log('req err', err);
      return null;
    })
    .then((v) => {
      clearTimeout(timer);
      return v;
    });
}

/**
 * The content type the beacon is sent as, and the reason it is sent at all (#173).
 *
 * `text/plain` is one of the three values that make a request **CORS-simple**
 * (with `application/x-www-form-urlencoded` and `multipart/form-data`); every
 * other value, `application/json` included, makes the browser preflight. The
 * page is always cross-origin — the snippet runs on the site and calls
 * `https://api.<domain>` — so that preflight is not hypothetical, and a
 * `sendBeacon` request that requires one is **discarded**: the browser queues
 * it, answers `true`, issues the `OPTIONS`, and then never sends the POST. Four
 * real page loads produced four `OPTIONS /v1/events` and zero POSTs, for the
 * whole life of the endpoint — no browser had ever recorded a render.
 *
 * The body is still JSON; only the declared type changes. `/v1/events` parses
 * the bytes itself rather than through a body model precisely so the content
 * type is irrelevant to it (`mdfs/serve/events.py::parse_beacon`, which names
 * `text/plain;charset=UTF-8` as a case it must accept), and it is asserted
 * there — so this is a change to what the browser will *send*, not to what the
 * server will accept.
 *
 * The fallback below sends it too. A keepalive `fetch` does follow a preflight,
 * so `application/json` would work there — but it would spend a round trip the
 * request does not need, at the one moment (a page going away) when there may
 * not be one, and it would leave the endpoint's two transports disagreeing
 * about a property that has already cost a quarter of the product's telemetry.
 */
const BEACON_CONTENT_TYPE = 'text/plain';

/**
 * POST /v1/events (plan §7.2) — sendBeacon, falling back to keepalive fetch.
 *
 * The beacon carries `render_tag` alongside the id: the HMAC `/v1/decision`
 * minted over that decision id (#94). It is opaque here — the snippet never
 * reads or checks it, it only hands back what the service issued — and it is
 * what lets the service refuse a forged `decision_id` at the door instead of
 * writing a junk `renders` row and pruning it later.
 *
 * Both fields are checked here rather than at the two call sites in core.ts.
 * That is not only smaller: "a beacon we cannot sign is a beacon we do not
 * send" is one rule, and the version where each caller remembers it is the
 * version where the third caller does not. A response missing either field
 * would be refused by /v1/events anyway, so the request is pure waste.
 *
 * The `fetch` is a real fallback and not decoration, but only because the beacon
 * is CORS-simple (see `BEACON_CONTENT_TYPE`). It runs when `sendBeacon` is
 * absent, when it throws (a privacy extension replacing it), and when it returns
 * `false` — the queue is full, i.e. the browser is telling us it will not
 * deliver. What it cannot do is cover a `true` that the browser does not honour,
 * because `true` is returned before anything is sent and there is no later
 * signal of any kind: no promise, no event, no readable response. That was the
 * defect's whole shape, and the only fix for it is to send a request the browser
 * has no reason to drop — not a second transport behind a verdict that never
 * arrives.
 */
export function beacon(cfg: Config, res: DecisionResponse): void {
  const id = res.decision_id;
  const tag = res.render_tag;
  if (typeof id !== 'string' || !id || typeof tag !== 'string' || !tag) return;
  const url = cfg.api! + '/v1/events'; // only reached after a served decision
  const payload = JSON.stringify({ decision_id: id, event: 'render', render_tag: tag });
  try {
    const nav = navigator;
    if (typeof nav.sendBeacon === 'function') {
      const blob = new Blob([payload], { type: BEACON_CONTENT_TYPE });
      if (nav.sendBeacon(url, blob)) {
        // "queued", not "sent": all `true` means is that the browser accepted
        // the request for delivery. Logging it as a delivery is what made #173
        // invisible — the console said the beacon went, four times a page, while
        // the server received none of them. Nothing the page can observe says
        // more than this, so nothing it prints may claim more.
        log('beacon queued', id);
        return;
      }
    }
  } catch (err) {
    log('beacon err', err);
  }
  try {
    fetch(url, {
      method: 'POST',
      credentials: 'omit',
      keepalive: true,
      headers: { 'Content-Type': BEACON_CONTENT_TYPE },
      body: payload,
    }).catch(() => undefined);
    log('beacon fetch', id);
  } catch (err) {
    log('beacon err', err);
  }
}
